// src/composables/useReservationStatus.ts
import { ref, computed } from "vue";
var _reservationStatusState = {};
function useReservationStatus(calendarId) {
  const setReservationStatus = (status) => {
    if (calendarId === void 0) {
      _reservationStatusState.value = status;
    } else {
      _reservationStatusState = {
        calendarId: ref(status)
      };
    }
  };
  const reservationStatus = computed(() => {
    if (calendarId === void 0) {
      return _reservationStatusState.value;
    }
    return _reservationStatusState[calendarId];
  });
  return {
    setReservationStatus,
    reservationStatus
  };
}

// src/composables/useSelectedTimeSlot.ts
import { ref as ref2, computed as computed2 } from "vue";
var _selectedTimeSlot = ref2({
  __DEFAULT__: {
    selectedTimeSlot: void 0
  }
});
function useSelectedTimeSlot(calendarId) {
  if (calendarId !== void 0 && _selectedTimeSlot.value[calendarId] === void 0) {
    _selectedTimeSlot.value[calendarId] = {
      selectedTimeSlot: void 0
    };
  }
  const setSelectedTimeSlot = (timeSlot) => {
    if (calendarId === void 0) {
      _selectedTimeSlot.value.__DEFAULT__.selectedTimeSlot = timeSlot;
    } else {
      _selectedTimeSlot.value[calendarId].selectedTimeSlot = timeSlot;
    }
  };
  const selectedTimeSlot = computed2(() => {
    if (calendarId === void 0) {
      return _selectedTimeSlot.value.__DEFAULT__.selectedTimeSlot;
    } else {
      return _selectedTimeSlot.value[calendarId].selectedTimeSlot;
    }
  });
  return {
    setSelectedTimeSlot,
    selectedTimeSlot
  };
}

// src/composables/useLocations.ts
import { ref as ref3, computed as computed3 } from "vue";
var state = ref3({
  __DEFAULT__: []
});
function useLocations(calendarId) {
  const setLocations = (newLocations) => {
    if (newLocations) {
      if (calendarId === void 0) {
        state.value.__DEFAULT__ = newLocations;
      } else {
        state.value[calendarId] = newLocations;
      }
    }
  };
  const isPhoneCall = computed3(() => {
    if (calendarId) {
      if (state.value[calendarId] === void 0 || state.value[calendarId].length === 0) {
        return false;
      }
      return state.value[calendarId].some((location) => location.value === "phone");
    } else {
      if (state.value.__DEFAULT__ === void 0 || state.value.__DEFAULT__.length === 0) {
        return false;
      }
      return state.value.__DEFAULT__.some((location) => location.value === "phone");
    }
  });
  const locations = computed3(() => {
    if (calendarId === void 0) {
      return state.value.__DEFAULT__;
    }
    return state.value[calendarId];
  });
  return {
    locations,
    setLocations,
    isPhoneCall
  };
}

// src/defaultConfig.ts
var config = {
  token: "oG77Ft7Wv6v9stJTOw8cbMmW7zENDzXl",
  locale: {
    preset: "en",
    texts: {
      chooseDate: "Choose a date",
      noTimeSlotAvailable: "There is no time slot available for chosen month.",
      choosePreferredTime: "Choose preferred time",
      pickTime: "Pick a time"
    },
    confirmationForm: {
      confirmBooking: "Confirm booking",
      reschedulingEvent: "Rescheduling event",
      buttons: {
        confirmBooking: "Confirm booking",
        reschedule: "Reschedule",
        goBack: "Go back"
      },
      payments: {
        showBillingDetails: "Vyplnit faktura\u010Dn\xED \xFAdaje",
        price: "Price",
        cardNumber: "Card number",
        name: "Name",
        email: "Email",
        company: "Company",
        address: "Address",
        city: "City",
        zip: "Postal code",
        country: "Country",
        vatId: "Vat ID",
        crn: "CRN"
      }
    }
  },
  theme: {
    preset: "basic",
    borderRadius: "0.375rem"
  }
};
var defaultConfig_default = config;

// src/composables/useConfig.ts
import { ref as ref4, computed as computed4 } from "vue";

// src/utils/mergeObjects.ts
function mergeRecursive(obj1, obj2) {
  for (const p in obj2) {
    try {
      if (obj2[p].constructor == Object) {
        obj1[p] = mergeRecursive(obj1[p], obj2[p]);
      } else {
        obj1[p] = obj2[p];
      }
    } catch (e) {
      obj1[p] = obj2[p];
    }
  }
  return obj1;
}

// src/composables/useConfig.ts
import { reactifyObject } from "@vueuse/core";
var _config = ref4({
  __DEFAULT__: {
    config: defaultConfig_default
  }
});
function useConfig(calendarId) {
  if (calendarId !== void 0 && _config.value[calendarId] === void 0) {
    _config.value[calendarId] = {
      config: defaultConfig_default
    };
  }
  const setConfig = (cfg) => {
    if (calendarId === void 0) {
      _config.value.__DEFAULT__.config = reactifyObject(mergeRecursive(defaultConfig_default, cfg));
    } else {
      _config.value[calendarId].config = reactifyObject(mergeRecursive(defaultConfig_default, cfg));
    }
  };
  const config2 = computed4(() => {
    if (calendarId === void 0) {
      return _config.value.__DEFAULT__.config;
    } else {
      return _config.value[calendarId].config;
    }
  });
  return {
    config: config2,
    setConfig
  };
}

// src/api/api.ts
import { Ok, Err } from "ts-results";
var defaultBaseUrl = "https://api.zaptime.app/";
var book = async (options) => {
  const { email, token, timeSlot, firstName, lastName, seats = 1, baseUrl = defaultBaseUrl, phone, location, timezone: timezone2 } = options;
  try {
    const data = await fetch(getBookUrl(baseUrl), {
      method: "POST",
      body: JSON.stringify({
        start: timeSlot.start,
        end: timeSlot.end,
        email,
        seats,
        firstname: firstName,
        lastname: lastName,
        phone,
        location,
        timezone: timezone2,
        customFields: options.customFields
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token
      }
    }).then((response) => response.json());
    return data;
  } catch (err) {
    throw new Error("Booking time slot failed!");
  }
};
var reschedule = async ({
  start,
  end,
  uuid,
  timezone: timezone2,
  token,
  baseUrl = defaultBaseUrl
}) => {
  try {
    const data = await fetch(getRescheduleUrl(baseUrl, uuid), {
      method: "PUT",
      body: JSON.stringify({
        start,
        end,
        timezone: timezone2
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token
      }
    }).then((response) => response.json());
    return data;
  } catch (err) {
    throw new Error("Booking time slot failed!");
  }
};
var reserve = async (options) => {
  const { email, token, timeSlot, firstName, lastName, seats = 1, baseUrl = defaultBaseUrl, phone, location, timezone: timezone2 } = options;
  try {
    const data = await fetch(getReserveUrl(baseUrl), {
      method: "POST",
      body: JSON.stringify({
        start: timeSlot.start,
        end: timeSlot.end,
        email,
        seats,
        firstname: firstName,
        lastname: lastName,
        phone,
        location,
        timezone: timezone2,
        customFields: options.customFields
      }),
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token
      }
    }).then((response) => response.json());
    return data;
  } catch (err) {
    throw new Error("Reserving time slot failed!");
  }
};
var confirm = async (options) => {
  const { baseUrl = defaultBaseUrl, status, token, firstName, lastName, customFields, phone } = options;
  try {
    const data = await fetch(getConfirmUrl(baseUrl, status.uuid), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token
      },
      body: JSON.stringify({
        firstname: firstName,
        lastname: lastName,
        customFields,
        phone
      })
    }).then((response) => response.json());
    return data.success;
  } catch (err) {
    throw new Error("Time slot confirmation failed!");
  }
};
var cancel = async (token, status, baseUrl = defaultBaseUrl) => {
  try {
    const data = await fetch(getCancelUrl(baseUrl, status.uuid), {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: "Bearer " + token
      }
    }).then((resposne) => resposne.json());
    return data.success;
  } catch (err) {
    throw new Error("Reserving time slot failed!");
  }
};
var getAvailableTimeSlots = async (token, from, until, baseUrl = defaultBaseUrl) => {
  try {
    const res = await fetch(
      getAvailableTimeSlotsUrl(baseUrl) + "?" + new URLSearchParams({
        from,
        until
      }),
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + token
        }
      }
    );
    if (res.status === 403) {
      throw new Error("Disabled");
    }
    const data = await res.json();
    return data.data;
  } catch (err) {
    if (err.message === "Disabled") {
      throw new Error("Disabled");
    }
    throw new Error("Getting available time slots failed!");
  }
};
async function fetchRemoteConfig(token, baseUrl = defaultBaseUrl, reservationUuid) {
  if (token) {
    try {
      const res = await fetch(baseUrl + "event-types/init", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + token
        },
        body: JSON.stringify({
          reservationUuid
        })
      });
      const data = await res.json();
      if (data.success) {
        return new Ok(data.data);
      }
    } catch (e) {
      return new Err("invalidToken");
    }
  }
  return new Err("invalidToken");
}
function getBookUrl(baseUrl) {
  return baseUrl + "reservations";
}
function getRescheduleUrl(baseUrl, uuid) {
  return baseUrl + "reservations/" + uuid;
}
function getReserveUrl(baseUrl) {
  return baseUrl + "reservations/prepare";
}
function getConfirmUrl(baseUrl, uuid) {
  return baseUrl + "reservations/" + uuid + "/confirm";
}
function getCancelUrl(baseUrl, uuid) {
  return baseUrl + "reservations/" + uuid;
}
function getAvailableTimeSlotsUrl(baseUrl) {
  return baseUrl + "time-slots";
}

// src/composables/useCurrentTimezone.ts
import { ref as ref5 } from "vue";
var clientOriginalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
var timezone = ref5(clientOriginalTimezone);
function useCurrentTimezone() {
  const setTimezone = (tz) => {
    timezone.value = tz;
  };
  return {
    clientOriginalTimezone,
    timezone,
    setTimezone
  };
}

// src/composables/useReservationReschedule.ts
import { ref as ref6, computed as computed5 } from "vue";
var selectedReservation = ref6({
  __DEFAULT__: {
    reservation: void 0
  }
});
function useReservationReschedule(calendarId) {
  if (calendarId !== void 0 && selectedReservation.value[calendarId] === void 0) {
    selectedReservation.value[calendarId] = {
      reservation: void 0
    };
  }
  const setSelectedReservation = (timeSlot) => {
    if (calendarId === void 0) {
      selectedReservation.value.__DEFAULT__.reservation = timeSlot;
    } else {
      selectedReservation.value[calendarId].reservation = timeSlot;
    }
  };
  const reservation = computed5(() => {
    if (calendarId === void 0) {
      return selectedReservation.value.__DEFAULT__.reservation;
    } else {
      return selectedReservation.value[calendarId].reservation;
    }
  });
  return {
    setSelectedReservation,
    reservation
  };
}

// src/composables/useApi.ts
var book2 = async (options) => {
  const { email, firstName, lastName, seats = 1, calendarId, phone, location, customFields } = options;
  const { selectedTimeSlot } = useSelectedTimeSlot(calendarId);
  const { config: config2 } = useConfig(calendarId);
  const { timezone: timezone2 } = useCurrentTimezone();
  const { locations: internalLocations } = useLocations(calendarId);
  if (selectedTimeSlot.value !== void 0 && config2.value !== void 0) {
    try {
      const res = await book({
        email,
        token: config2.value.token,
        timeSlot: selectedTimeSlot.value,
        firstName,
        lastName,
        seats,
        baseUrl: config2.value.apiBaseUrl,
        phone,
        location: location ?? internalLocations.value[0],
        timezone: timezone2.value,
        customFields
      });
      if (config2.value.redirectAfterBookingUrl !== void 0) {
        window.location.href = config2.value.redirectAfterBookingUrl;
      }
      return res;
    } catch (e) {
      throw new Error("Booking a time slot failed because time slot was not selected!");
    }
  }
  throw new Error("Booking a time slot failed because time slot was not selected!");
};
var reserve2 = async (options) => {
  const { email, firstName, lastName, seats = 1, calendarId, location, phone, customFields } = options;
  const { selectedTimeSlot } = useSelectedTimeSlot(calendarId);
  const { config: config2 } = useConfig(calendarId);
  const { setReservationStatus } = useReservationStatus(calendarId);
  const { timezone: timezone2 } = useCurrentTimezone();
  const { locations: internalLocations } = useLocations(calendarId);
  if (selectedTimeSlot.value !== void 0 && config2.value !== void 0) {
    const data = await reserve({
      email,
      token: config2.value.token,
      timeSlot: selectedTimeSlot.value,
      firstName,
      lastName,
      seats,
      baseUrl: config2.value.apiBaseUrl,
      phone,
      location: location ?? internalLocations.value[0],
      timezone: timezone2.value,
      customFields
    });
    setReservationStatus(data.data);
    return data;
  }
  throw new Error("Booking a time slot failed because time slot was not selected!");
};
var confirm2 = async (options) => {
  const { reservationStatus } = useReservationStatus(options?.calendarId);
  const { config: config2 } = useConfig(options?.calendarId);
  if (reservationStatus.value !== void 0) {
    return await confirm({
      token: config2.value.token,
      status: reservationStatus.value,
      baseUrl: config2.value.apiBaseUrl,
      firstName: options?.firstName,
      lastName: options?.lastName,
      phone: options?.phone,
      customFields: options?.customFields
    });
  }
  throw new Error("Confirming a time slot failed because time slot was not reserved!");
};
var cancel2 = async (calendarId) => {
  const { reservationStatus } = useReservationStatus(calendarId);
  const { config: config2 } = useConfig(calendarId);
  if (reservationStatus.value !== void 0) {
    return await cancel(config2.value.token, reservationStatus.value, config2.value.apiBaseUrl);
  }
  return false;
};
var reschedule2 = async (calendarId) => {
  const { reservation } = useReservationReschedule(calendarId);
  const { config: config2 } = useConfig(calendarId);
  const { selectedTimeSlot } = useSelectedTimeSlot(calendarId);
  const { timezone: timezone2 } = useCurrentTimezone();
  if (reservation.value !== void 0 && selectedTimeSlot.value !== void 0) {
    return await reschedule({
      start: selectedTimeSlot.value.start,
      end: selectedTimeSlot.value.end,
      token: config2.value.token,
      uuid: reservation.value?.uuid,
      baseUrl: config2.value.apiBaseUrl,
      timezone: timezone2.value
    });
  }
  throw new Error("Rescheduling a time slot failed because time slot was not selected!");
};
var fetchRemoteConfiguration = async (token, apiBaseUrl, reservationUuid) => {
  return await fetchRemoteConfig(token, apiBaseUrl, reservationUuid);
};

// src/composables/useCalendar.ts
import { computed as computed6, reactive, ref as ref7, watch } from "vue";
import { addMonths, format as format3, isPast as isPast2, isFuture, differenceInCalendarMonths } from "date-fns";

// src/utils/calendar.ts
import { getDaysInMonth, startOfMonth, subMonths, lastDayOfMonth, subDays, endOfMonth, addDays, isPast, isToday, isThisMonth } from "date-fns";
import { format as format2, utcToZonedTime } from "date-fns-tz";

// src/utils/localeLogic.ts
import { format } from "date-fns";
var getHeaders = (locale, dfnsConfig) => {
  let sun = "";
  if (locale && locale.headers && locale.headers.sun) {
    sun = locale.headers.sun;
  } else {
    sun = format(new Date(2020, 11, 6), "cccccc", dfnsConfig);
  }
  let mon = "";
  if (locale && locale.headers && locale.headers.mon) {
    mon = locale.headers.mon;
  } else {
    mon = format(new Date(2020, 11, 7), "cccccc", dfnsConfig);
  }
  let tue = "";
  if (locale && locale.headers && locale.headers.tue) {
    tue = locale.headers.tue;
  } else {
    tue = format(new Date(2020, 11, 8), "cccccc", dfnsConfig);
  }
  let wed = "";
  if (locale && locale.headers && locale.headers.wed) {
    wed = locale.headers.wed;
  } else {
    wed = format(new Date(2020, 11, 9), "cccccc", dfnsConfig);
  }
  let thu = "";
  if (locale && locale.headers && locale.headers.thu) {
    thu = locale.headers.thu;
  } else {
    thu = format(new Date(2020, 11, 10), "cccccc", dfnsConfig);
  }
  let fri = "";
  if (locale && locale.headers && locale.headers.fri) {
    fri = locale.headers.fri;
  } else {
    fri = format(new Date(2020, 11, 11), "cccccc", dfnsConfig);
  }
  let sat = "";
  if (locale && locale.headers && locale.headers.sat) {
    sat = locale.headers.sat;
  } else {
    sat = format(new Date(2020, 11, 12), "cccccc", dfnsConfig);
  }
  const autoHeaders = [sun, mon, tue, wed, thu, fri, sat];
  const startOfTheWeekIndex = getStartOfTheWeekIndex(locale);
  if (startOfTheWeekIndex !== 0) {
    const elementsToBeMoved = [];
    for (let i = 0; i < startOfTheWeekIndex; i++) {
      const shifted = autoHeaders.shift();
      elementsToBeMoved.push(shifted);
    }
    for (const shifted of elementsToBeMoved) {
      if (shifted !== void 0) {
        autoHeaders.push(shifted);
      }
    }
  }
  return autoHeaders;
};
var getStartOfTheWeekIndex = (locale) => {
  const dayToIndex = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6
  };
  if (locale.startDayOfWeek) {
    return dayToIndex[locale.startDayOfWeek];
  }
  return 0;
};

// src/utils/calendar.ts
var getTimeSlotsForDivenDate = (date2, timeSlots, timeZone) => {
  const collectedTimeSlots = [];
  for (const timeSlot of timeSlots) {
    if (format2(utcToZonedTime(date2, timeZone), "d", { timeZone }) === format2(utcToZonedTime(new Date(timeSlot.start), timeZone), "d", { timeZone })) {
      collectedTimeSlots.push(timeSlot);
    }
  }
  if (collectedTimeSlots.length > 0) {
    return collectedTimeSlots;
  }
  return void 0;
};
var getDays = async (date2, dfnsConfig, zapTimeConfig, timezone2) => {
  const dayCountInMonth = getDaysInMonth(date2);
  const startDateOfTheMonth = startOfMonth(date2);
  let hasAnyTimeSlot = false;
  const timeSlots = await getAvailableTimeSlots(zapTimeConfig.token, getStartDate(date2, zapTimeConfig, timezone2), format2(endOfMonth(date2), "yyyy-MM-dd", { timeZone: timezone2 }), zapTimeConfig.apiBaseUrl);
  if (Object.keys(timeSlots).length > 0) {
    hasAnyTimeSlot = true;
  }
  const startOfTheWeekIndex = zapTimeConfig && zapTimeConfig.locale !== void 0 ? getStartOfTheWeekIndex(zapTimeConfig.locale) : 0;
  const numberOfDay = parseInt(format2(startDateOfTheMonth, "i", { ...dfnsConfig, timeZone: timezone2 })) - startOfTheWeekIndex + 1;
  const previousMonth = subMonths(startDateOfTheMonth, 1);
  const lastDateOfPreviousMonth = lastDayOfMonth(previousMonth);
  const days = [];
  for (let k = 1; k < numberOfDay; k++) {
    days.unshift({
      label: format2(subDays(lastDateOfPreviousMonth, k - 1), "d", { timeZone: timezone2 }),
      isPast: true
    });
  }
  for (let i = 1; i <= dayCountInMonth; i++) {
    const iteratedDate = addDays(startDateOfTheMonth, i - 1);
    days.push({
      label: i.toString(),
      date: iteratedDate,
      isPast: isPast(iteratedDate) && !isToday(iteratedDate),
      // TODO: isCurrentMonth possibly unnecessary seems like it's always true
      isCurrentMonth: true,
      isToday: isToday(iteratedDate),
      timeSlots: getTimeSlotsForDivenDate(iteratedDate, timeSlots, timezone2)
    });
  }
  let nextMonthRemainingDays = 0;
  if (days.length <= 35) {
    nextMonthRemainingDays = 35 - dayCountInMonth - numberOfDay + 1 + startOfTheWeekIndex;
  } else {
    nextMonthRemainingDays = 42 - dayCountInMonth - numberOfDay + 1 + startOfTheWeekIndex;
  }
  for (let j = 1; j <= nextMonthRemainingDays - startOfTheWeekIndex; j++) {
    days.push({
      label: j.toString(),
      timeSlots: void 0,
      isPast: true
    });
  }
  return { days, hasAnyTimeSlot };
};
var getStartDate = (date2, zapTimeConfig, timezone2) => {
  if (isThisMonth(date2) && zapTimeConfig.closestBookableDay !== void 0) {
    return format2(addDays(date2, zapTimeConfig.closestBookableDay), "yyyy-MM-dd", { timeZone: timezone2 });
  }
  return format2(startOfMonth(date2), "yyyy-MM-dd", { timeZone: timezone2 });
};

// src/utils/dfnsConfig.ts
var getDfnsConfig = async (locale) => {
  let dateFnsLocale = void 0;
  if (locale === void 0) {
    dateFnsLocale = await import("date-fns/locale/en-US");
    const dfnsConf2 = {
      locale: dateFnsLocale.default
    };
    return dfnsConf2;
  }
  if (locale === "cs") {
    dateFnsLocale = await import("date-fns/locale/cs");
  } else if (locale === "sk") {
    dateFnsLocale = await import("date-fns/locale/sk");
  } else if (locale === "pl") {
    dateFnsLocale = await import("date-fns/locale/pl");
  } else if (locale === "de") {
    dateFnsLocale = await import("date-fns/locale/de");
  } else if (locale === "pt") {
    dateFnsLocale = await import("date-fns/locale/pt");
  } else if (locale === "es") {
    dateFnsLocale = await import("date-fns/locale/es");
  } else if (locale === "ja") {
    dateFnsLocale = await import("date-fns/locale/ja");
  } else if (locale === "zh") {
    dateFnsLocale = await import("date-fns/locale/zh-CN");
  } else if (locale === "tr") {
    dateFnsLocale = await import("date-fns/locale/tr");
  } else if (locale === "sv") {
    dateFnsLocale = await import("date-fns/locale/sv");
  } else if (locale === "nl") {
    dateFnsLocale = await import("date-fns/locale/nl");
  } else if (locale === "it") {
    dateFnsLocale = await import("date-fns/locale/it");
  } else if (locale === "fi") {
    dateFnsLocale = await import("date-fns/locale/fi");
  } else if (locale === "ro") {
    dateFnsLocale = await import("date-fns/locale/ro");
  } else if (locale === "ko") {
    dateFnsLocale = await import("date-fns/locale/ko");
  } else if (locale === "vi") {
    dateFnsLocale = await import("date-fns/locale/vi");
  } else {
    dateFnsLocale = await import("date-fns/locale/en-US");
  }
  const dfnsConf = {
    locale: dateFnsLocale.default
  };
  return dfnsConf;
};

// src/composables/useCalendar.ts
var date = ref7(/* @__PURE__ */ new Date());
var initCalendarState = {
  date: /* @__PURE__ */ new Date(),
  days: [],
  timeSlots: [],
  monthHasTimeSlots: false,
  selectedDay: null,
  loading: true,
  headers: [],
  dfnsConfig: void 0,
  attendeeState: void 0
};
var _state = reactive({
  __DEFAULT__: { ...initCalendarState }
});
var useCalendar_default = (calendarId) => {
  if (calendarId !== void 0) {
    if (_state[calendarId] === void 0) {
      _state[calendarId] = { ...initCalendarState };
    }
  }
  const { config: config2 } = useConfig(calendarId);
  const { selectedTimeSlot, setSelectedTimeSlot } = useSelectedTimeSlot(calendarId);
  const { timezone: timezone2 } = useCurrentTimezone();
  const selectTimeSlot = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
  };
  const state5 = computed6(() => {
    if (calendarId === void 0) {
      return _state["__DEFAULT__"];
    } else {
      return _state[calendarId];
    }
  });
  const setState = (key, value) => {
    if (calendarId === void 0) {
      _state["__DEFAULT__"][key] = value;
    } else {
      _state[calendarId][key] = value;
    }
  };
  function getFirstAvailableDayWithTimeSlot(days) {
    for (const day of days) {
      if (day.timeSlots !== void 0 && day.timeSlots.length > 0 && !day.isPast) {
        return day;
      }
    }
  }
  const getDays2 = async () => {
    setState("loading", true);
    setState("timeSlots", []);
    setState("selectedDay", null);
    if (state5.value.dfnsConfig !== void 0 && state5.value.dfnsConfig !== null) {
      const { days, hasAnyTimeSlot } = await getDays(date.value, state5.value.dfnsConfig, config2.value, timezone2.value);
      if (hasAnyTimeSlot) {
        const firstAvailableDayWithTimeSlot = getFirstAvailableDayWithTimeSlot(days);
        if (firstAvailableDayWithTimeSlot !== void 0) {
          setState("selectedDay", firstAvailableDayWithTimeSlot);
          if (firstAvailableDayWithTimeSlot.timeSlots !== void 0) {
            setState("timeSlots", firstAvailableDayWithTimeSlot.timeSlots);
          }
        }
      }
      setState("monthHasTimeSlots", hasAnyTimeSlot);
      setState("days", days);
      setState("loading", false);
    }
  };
  const isSelectedDay = (day) => {
    return day === state5.value.selectedDay;
  };
  const dayClicked = (day) => {
    setState("selectedDay", day);
    if (day.timeSlots !== void 0) {
      setState("timeSlots", day.timeSlots);
    }
  };
  const dayHasTimeSlot = (day) => {
    return day.timeSlots !== void 0;
  };
  const isSelected = (timeSlot) => {
    if (selectedTimeSlot.value === void 0) {
      return false;
    }
    return selectedTimeSlot.value.start === timeSlot.start;
  };
  const next = async () => {
    if (nextDisabled.value) {
      return;
    }
    setState("timeSlots", []);
    setState("loading", true);
    date.value = addMonths(date.value, 1);
    await getDays2();
    setState("loading", false);
  };
  const prev = async () => {
    if (prevDisabled.value) {
      return;
    }
    setState("timeSlots", []);
    setState("loading", true);
    date.value = addMonths(date.value, -1);
    await getDays2();
    setState("loading", false);
  };
  const prevDisabled = computed6(() => {
    const now = /* @__PURE__ */ new Date();
    const newDate = addMonths(date.value, -1);
    const distance = differenceInCalendarMonths(now, newDate);
    if (isPast2(newDate) && config2.value.min !== void 0) {
      if (distance > config2.value.min) {
        return true;
      }
    }
    return false;
  });
  const nextDisabled = computed6(() => {
    const now = /* @__PURE__ */ new Date();
    const newDate = addMonths(date.value, 1);
    const distance = differenceInCalendarMonths(newDate, now);
    if (isFuture(newDate) && config2.value.max !== void 0) {
      if (distance > config2.value.max) {
        return true;
      }
    }
    return false;
  });
  const currentYear = computed6(() => {
    return format3(date.value, "y");
  });
  const monthName = computed6(() => {
    return format3(date.value, "LLLL", {
      locale: state5.value.dfnsConfig?.locale
    });
  });
  watch([() => config2.value.locale?.preset, () => config2.value.closestBookableDay, () => config2.value.locale?.startDayOfWeek], async () => {
    await setLocales();
    await getDays2();
  });
  const setLocales = async () => {
    if (config2.value && config2.value.locale) {
      state5.value.dfnsConfig = await getDfnsConfig(config2.value.locale.preset || "en");
      setState("headers", getHeaders(config2.value.locale, state5.value.dfnsConfig));
    }
  };
  const init = async () => {
    if (state5.value.days.length === 0) {
      await setLocales();
      await getDays2();
    }
  };
  return {
    getDays: getDays2,
    init,
    selectTimeSlot,
    nextDisabled,
    prevDisabled,
    next,
    prev,
    dayClicked,
    dayHasTimeSlot,
    isSelected,
    isSelectedDay,
    currentYear,
    monthName,
    config: config2,
    state: state5.value
  };
};

// src/composables/useHourCycle.ts
import { ref as ref8 } from "vue";
var clientOriginalHourCycle = "h23";
if (typeof window !== "undefined") {
  clientOriginalHourCycle = Intl.DateTimeFormat(navigator.language, { hour: "numeric" }).resolvedOptions().hourCycle;
}
var hourCycle = ref8(clientOriginalHourCycle);
function useHourCycle() {
  const setHourCycle = (hc) => {
    hourCycle.value = hc;
  };
  return {
    clientOriginalHourCycle,
    setHourCycle,
    hourCycle
  };
}

// src/composables/useStripeConfig.ts
import { ref as ref9, computed as computed7 } from "vue";
var state2 = ref9({
  __DEFAULT__: void 0
});
function useStripeConfig(calendarId) {
  const setStripeConfig = (stripeConfig2) => {
    if (calendarId === void 0) {
      state2.value.__DEFAULT__ = stripeConfig2;
    } else {
      state2.value[calendarId] = stripeConfig2;
    }
  };
  const stripeConfig = computed7(() => {
    if (calendarId === void 0) {
      return state2.value.__DEFAULT__;
    }
    return state2.value[calendarId];
  });
  return {
    setStripeConfig,
    stripeConfig
  };
}

// src/composables/useBookingForm.ts
import { ref as ref10, computed as computed8 } from "vue";
var state3 = ref10({
  __DEFAULT__: []
});
var knownFieldsByMergeTag = ["FIRST_NAME", "LAST_NAME", "EMAIL", "PHONE"];
function useBookingForm(calendarId) {
  const setBookingForm = (customFields) => {
    if (calendarId === void 0) {
      state3.value.__DEFAULT__ = customFields;
    } else {
      state3.value[calendarId] = customFields;
    }
  };
  const bookingForm = computed8(() => {
    if (calendarId === void 0) {
      return state3.value.__DEFAULT__;
    }
    return state3.value[calendarId];
  });
  function setCustomFieldValue(uuid, value) {
    const field = bookingForm.value.find((field2) => field2.uuid === uuid);
    if (field === void 0) {
      return;
    }
    field.value = value;
  }
  function collectKnownFields() {
    const firstName = bookingForm.value.find((field) => field.mergeTag === "FIRST_NAME");
    const lastName = bookingForm.value.find((field) => field.mergeTag === "LAST_NAME");
    const email = bookingForm.value.find((field) => field.mergeTag === "EMAIL");
    const phone = bookingForm.value.find((field) => field.mergeTag === "PHONE");
    return {
      email: email?.value ? String(email.value) : "",
      firstName: firstName?.value ? String(firstName.value) : void 0,
      lastName: lastName?.value ? String(lastName.value) : void 0,
      phone: phone?.value ? String(phone.value) : void 0
    };
  }
  function collectUnknownFields() {
    const customFieldsCollected = [];
    const unknownCustomFields = bookingForm.value.filter((field) => !knownFieldsByMergeTag.includes(field.mergeTag));
    for (const unknownCustomField of unknownCustomFields) {
      customFieldsCollected.push({
        uuid: unknownCustomField.uuid,
        value: unknownCustomField.value
      });
    }
    return customFieldsCollected;
  }
  function collectFormValues() {
    const { firstName, lastName, email, phone } = collectKnownFields();
    return {
      firstName,
      lastName,
      email,
      phone,
      customFields: collectUnknownFields()
    };
  }
  return {
    setBookingForm,
    bookingForm,
    setCustomFieldValue,
    collectFormValues
  };
}

// src/composables/useBillingAddress.ts
import { ref as ref11, computed as computed9 } from "vue";
var state4 = ref11({
  __DEFAULT__: {
    name: "",
    email: "",
    company: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
    crn: "",
    vatId: ""
  }
});
function useBillingAddress(calendarId) {
  const setBillingAddress = (billingAddress2) => {
    if (calendarId === void 0) {
      state4.value.__DEFAULT__ = billingAddress2;
    } else {
      state4.value[calendarId] = billingAddress2;
    }
  };
  const billingAddress = computed9(() => {
    if (calendarId === void 0) {
      return state4.value.__DEFAULT__;
    }
    return state4.value[calendarId];
  });
  function updateBillingAddressField(field, value) {
    if (calendarId === void 0) {
      state4.value.__DEFAULT__[field] = value;
    } else {
      state4.value[calendarId][field] = value;
    }
  }
  return {
    setBillingAddress,
    billingAddress,
    updateBillingAddressField
  };
}

// src/composables/useDateFormatters.ts
import { parseISO } from "date-fns";
import { format as format4, utcToZonedTime as utcToZonedTime2 } from "date-fns-tz";
var dateFnsConfig = void 0;
var useDateFormatters = () => {
  const { timezone: timezone2 } = useCurrentTimezone();
  const { hourCycle: hourCycle2 } = useHourCycle();
  async function loadDateFnsConfig(locale) {
    dateFnsConfig = await getDfnsConfig(locale);
  }
  const getFormattedTime = (date2) => {
    if (hourCycle2.value === "h11") {
      return format4(utcToZonedTime2(parseISO(date2), timezone2.value), "h:mmaaa", dateFnsConfig);
    } else {
      return format4(utcToZonedTime2(parseISO(date2), timezone2.value), "H:mm", dateFnsConfig);
    }
  };
  const getFormattedDay = (date2) => {
    return format4(utcToZonedTime2(parseISO(date2), timezone2.value), "EEEE", { ...dateFnsConfig, timeZone: timezone2.value });
  };
  const getFormattedDayInMonth = (date2) => {
    return format4(utcToZonedTime2(parseISO(date2), timezone2.value), "PPPP", { ...dateFnsConfig, timeZone: timezone2.value });
  };
  return {
    getFormattedTime,
    getFormattedDay,
    getFormattedDayInMonth,
    loadDateFnsConfig
  };
};
export {
  book2 as book,
  cancel2 as cancel,
  confirm2 as confirm,
  fetchRemoteConfiguration,
  mergeRecursive as mergeObjects,
  reschedule2 as reschedule,
  reserve2 as reserve,
  useBillingAddress,
  useBookingForm,
  useCalendar_default as useCalendar,
  useConfig,
  useCurrentTimezone,
  useDateFormatters,
  useHourCycle,
  useLocations,
  useReservationReschedule,
  useSelectedTimeSlot,
  useStripeConfig
};
